import { BadImplementationException, cloneObj, compare, exists, ImplementationMissingException, MissingDataException, Module, tsValidate } from '@nu-art/ts-common';
import { spotlightMode_Disposition, spotlightMode_EditFindings, spotLightMode_PhysicianNotes, spotlightMode_ResolveOrder, spotlightMode_Support, spotlightMode_Survey, spotlightMode_Wizard, } from './types.js';
import { ColumnKey_LeftColumn, ColumnKey_MiddleColumn, ColumnKey_RightColumn, ColumnRatio_OneThird, defaultColumnWidthUpdate, MiddleColumn_Findings, MiddleColumn_Spotlight, } from '@app/advisor-v5-shared';
import { dispatch_OnSpotlightMode } from './dispatcher.js';
import { ModuleFE_EMInterface } from '@app/advisor-frontend';
import { getNoteTemplateId } from '../../_utils/get-note-template-id.js';
import { DBDef_RuntimeSmartNote } from '@app/advisor-shared';
import { ModuleFE_AdvisorBoard } from '../../_enum.js';
class ModuleFE_Spotlight_Class extends Module {
    spotlightMode;
    //######################### Internal Logic #########################
    __onAdvisorReset() {
        delete this.spotlightMode;
    }
    assertModeData = (mode, data) => {
        switch (mode) {
            case spotlightMode_Wizard: {
                const _data = data;
                if (!_data.wizardId)
                    throw new MissingDataException('Data for mode wizard missing property "wizardId"');
                break;
            }
            case spotlightMode_Disposition: {
                const _data = data;
                const missingProperties = [];
                if (!_data.dispositionType)
                    missingProperties.push('dispositionType');
                if (missingProperties.length)
                    throw new MissingDataException(`Data for mode disposition is missing the following properties: ${missingProperties.join(', ')}`);
                break;
            }
            case spotLightMode_PhysicianNotes: {
                const _data = data;
                const validationErrors = tsValidate(_data.note, DBDef_RuntimeSmartNote.modifiablePropsValidator);
                if (validationErrors) {
                    this.logError('Incorrect data in smart note', validationErrors);
                    throw new BadImplementationException('Incorrect data in smart note');
                }
                break;
            }
            case spotlightMode_ResolveOrder: {
                const _data = data;
                const missingProperties = [];
                if (!_data.dbOrderId)
                    missingProperties.push('dbOrderId');
                if (!_data.rtOrderId)
                    missingProperties.push('rtOrderId');
                if (missingProperties.length)
                    throw new MissingDataException(`Data for mode "resolve-order" is missing the following properties: ${missingProperties.join(', ')}`);
                break;
            }
            case spotlightMode_EditFindings: {
                const _data = data;
                if (!_data.findings)
                    throw new MissingDataException(`Data for mode "edit-findings" is missing property: findings`);
                break;
            }
            case spotlightMode_Support:
            case spotlightMode_Survey: {
                break; //nothing to assert
            }
            default:
                throw new ImplementationMissingException(`Missing assertion for mode ${mode}`);
        }
        return data;
    };
    validateData = (mode, data) => {
        if (exists(this.spotlightMode?.data) && compare(this.spotlightMode?.data, data))
            return false;
        try {
            this.assertModeData(mode, data);
            return true;
        }
        catch (err) {
            this.logError(err);
            return false;
        }
    };
    setData = (mode, data) => {
        if (!mode) {
            delete this.spotlightMode;
            ModuleFE_AdvisorBoard.updateColumnsWidth(defaultColumnWidthUpdate);
        }
        else
            this.spotlightMode = { mode, data };
        dispatch_OnSpotlightMode.dispatchAll();
    };
    //######################### Public Methods - Get #########################
    getSpotlightColumnState = (mode) => {
        switch (mode) {
            case spotLightMode_PhysicianNotes:
            case spotlightMode_Survey:
            case spotlightMode_Disposition:
            case spotlightMode_Wizard:
            case spotlightMode_Support:
            case spotlightMode_ResolveOrder:
            case spotlightMode_EditFindings:
                return MiddleColumn_Spotlight;
            default:
                return MiddleColumn_Findings;
        }
    };
    getSpotlightColumnWidth = (mode) => {
        switch (mode) {
            case spotlightMode_Support:
            case spotlightMode_Wizard:
                return {
                    [ColumnKey_LeftColumn]: { width: ColumnRatio_OneThird },
                    [ColumnKey_MiddleColumn]: { width: 2 * ColumnRatio_OneThird },
                    [ColumnKey_RightColumn]: { width: 0 },
                };
            default:
                return defaultColumnWidthUpdate;
        }
    };
    getSpotlightData = () => this.spotlightMode ? cloneObj(this.spotlightMode) : undefined;
    //######################### Public Methods - Set #########################
    closeSpotlight = () => {
        if (!this.spotlightMode)
            return;
        this.setData();
    };
    openWizard = (data) => {
        if (!this.validateData(spotlightMode_Wizard, data))
            return;
        this.setData(spotlightMode_Wizard, data);
    };
    openDisposition = async (data) => {
        const noteTemplateId = getNoteTemplateId();
        if (!noteTemplateId)
            return;
        try {
            const response = await ModuleFE_EMInterface.vv1.smartNote.get({ noteTemplateId }).executeSync();
            const _data = {
                dispositionType: data.dispositionType,
                protocolId: data.protocolId,
                physicianNote: response.note,
                additionalProtocolIds: data.additionalProtocolIds,
            };
            if (!this.validateData(spotlightMode_Disposition, _data))
                return;
            this.setData(spotlightMode_Disposition, _data);
        }
        catch (err) {
            this.logError('Error while getting physician note', err);
            throw err;
        }
    };
    openPhysicianNotes = (data) => {
        if (!this.validateData(spotLightMode_PhysicianNotes, data))
            return;
        this.setData(spotLightMode_PhysicianNotes, data);
    };
    openSurvey = (data) => {
        if (!this.validateData(spotlightMode_Survey, data))
            return;
        this.setData(spotlightMode_Survey, data);
    };
    openSupport = (data) => {
        if (!this.validateData(spotlightMode_Support, data))
            return;
        this.setData(spotlightMode_Support, data);
    };
    openResolveOrder = (data) => {
        if (!this.validateData(spotlightMode_ResolveOrder, data))
            return;
        this.setData(spotlightMode_ResolveOrder, data);
    };
    openEditFindings = (data) => {
        if (!this.validateData(spotlightMode_EditFindings, data))
            return;
        this.setData(spotlightMode_EditFindings, data);
    };
}
export const ModuleFE_Spotlight = new ModuleFE_Spotlight_Class();
//# sourceMappingURL=ModuleFE_Spotlight.js.map